import type { Locale } from "./types";

export type Dict = typeof id;

export const id = {
  brand: "Kalender Indonesia",
  nav: {
    monthNow: "Bulan ini",
    today: "Hari ini",
    calculator: "Kalkulator",
    weton: "Cek Weton",
    about: "Tentang",
    sitemap: "Peta situs",
    year: "Tahun",
    tools: "Kalkulator & Tools",
    hijri: "Kalender Hijriah",
    important: "Hari Penting",
    contact: "Kontak",
    redDates: "Tanggal Merah",
    menu: "Menu",
    yearOf: (y: number) => `Kalender ${y}`,
  },
  common: {
    home: "Beranda",
    calendar: "Kalender",
    nationalHolidays: "Libur Nasional",
    jointLeave: "Cuti Bersama",
    redDates: "Tanggal Merah",
    importantDays: "Hari Penting",
    workingDay: "Hari kerja biasa",
    estimatedData: "data perkiraan",
    legendLibur: "Libur Nasional",
    legendCuti: "Cuti Bersama",
    legendHari: "Hari Penting",
    legendToday: "Hari ini",
    legendCaption: "Keterangan",
    hijriCalendar: "Kalender Hijriah",
    hijriCalendarFull: "Kalender Islam (Hijriah)",
    gregorianCalendar: "Kalender Masehi",
    viewInHijri: "Lihat di Hijriah",
    viewInGregorian: "Lihat di Masehi",
    importantIslamicDays: "Hari Penting Islam",
    hijriYear: (y: number) => `${y} H`,
    longWeekend: "Long weekend",
    seeDetail: "Lihat detail",
    quickNav: "Navigasi cepat",
    pickYear: "Pilih tahun",
    otherYears: "Tahun lain",
    relatedPages: "Halaman terkait",
    detail: "Detail",
    download: "Unduh",
    downloadPdf: "Unduh PDF",
    selectMonth: "Bulan lain di",
    next: "Berikutnya",
    prev: "Sebelumnya",
    daysShort: ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"],
    days: ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"],
    months: [
      "Januari", "Februari", "Maret", "April", "Mei", "Juni",
      "Juli", "Agustus", "September", "Oktober", "November", "Desember",
    ],
    notFound: "Halaman tidak ditemukan",
    notFoundBody: "URL ini tidak cocok dengan format tahun, bulan, atau tanggal yang valid.",
    invalidYear: "Tahun tidak valid",
    error: "Terjadi kesalahan",
    retry: "Coba lagi",
    goHome: "Kembali ke beranda",
    summaryHoliday: (libur: number, cuti: number) =>
      `${libur} libur nasional, ${cuti} cuti bersama`,
    daysCount: (n: number) => `${n} hari`,
    holidayCount: (n: number) => `${n} tanggal libur nasional`,
    cutiCount: (n: number) => `${n} tanggal cuti bersama`,
    redCount: (l: number, c: number) =>
      `${l} libur nasional + ${c} cuti bersama`,
    monthOf: (m: string, y: number) => `${m} ${y}`,
    calendarOf: (label: string) => `Kalender ${label}`,
  },
  home: {
    heroTitle: "Kalender Indonesia",
    heroDesc:
      "Kalender Masehi, Hijriah, Jawa, dan Bali. Lengkap dengan tanggal merah, libur nasional, cuti bersama, weton, dan tools.",
    detailToday: "Detail hari ini",
    monthThis: "Kalender bulan ini",
    yearThis: (y: number) => `Kalender ${y}`,
    detailTodayDated: (dayName: string, d: number, month: string, y: number) =>
      `Hari ini: ${dayName}, ${d} ${month} ${y}`,
    monthThisDated: (month: string, y: number) => `Kalender ${month} ${y}`,
    todayLabel: "Hari Ini",
    tomorrowLabel: "Besok",
    tomorrowIs: (dayName: string, d: number, month: string, y: number) =>
      `Besok: ${dayName}, ${d} ${month} ${y}`,
    eyebrowToday: "Penanggalan Hari Ini",
    faqTitle: "Pertanyaan Umum",
    faqQ1: "Hari ini hari apa?",
    faqA1: (dayName: string, d: number, month: string, y: number) =>
      `Hari ini adalah hari ${dayName}, tanggal ${d} ${month} ${y}.`,
    faqQ2: "Besok tanggal berapa?",
    faqA2: (dayName: string, d: number, month: string, y: number) =>
      `Besok adalah hari ${dayName}, tanggal ${d} ${month} ${y}.`,
    faqQ3: "Kapan libur nasional berikutnya?",
    faqA3: (name: string, d: number, month: string, y: number) =>
      `Libur nasional berikutnya adalah ${name} pada ${d} ${month} ${y}.`,
    faqQ4: "Apa itu cuti bersama?",
    faqA4:
      "Cuti bersama adalah hari libur tambahan yang ditetapkan pemerintah Indonesia bersamaan dengan libur nasional, biasanya untuk Lebaran, Natal, dan Tahun Baru.",
    holidayThisMonth: "Libur bulan ini",
    noHoliday: "Tidak ada libur nasional/cuti bersama.",
    nextHoliday: (y: number) => `Libur berikutnya ${y}`,
    fullYear: (y: number) => `Kalender ${y} Lengkap`,
    fullYearDesc: (y: number) =>
      `Semua bulan tahun ${y}. Klik salah satu bulan untuk detail.`,
    seeYearPage: (y: number) => `Halaman tahun ${y}`,
    toolsCalc: "Kalkulator Hari",
    toolsCalcDesc:
      "Selisih tanggal, tambah/kurangi hari, hari kerja, nama hari, hitung umur.",
    toolsWeton: "Cek Weton Jawa",
    toolsWetonDesc:
      "Cek weton, neptu, pancasuda, wuku, tanggal Jawa, dan pranata mangsa.",
    pdfTitle: "Unduh Kalender PDF",
    pdfDesc:
      "Tersedia di setiap halaman tahun, bulan, dan tanggal. Pilih mode per bulan, setahun ringkas, atau detail satu hari. Bisa unggah logo sendiri.",
  },
  date: {
    redLabel: "Tanggal Merah",
    cutiLabel: "Cuti Bersama",
    eventToday: "Peristiwa Hari Ini",
    hijri: "Hijriah",
    jawa: "Jawa",
    bali: "Bali",
    chinese: "Tionghoa",
    pranata: "Pranata Mangsa",
    quickWeton: "Cek weton tanggal lain",
    quickCalc: "Hitung selisih hari",
    monthLabel: (m: string, y: number) => `Kalender ${m} ${y}`,
    yearLabel: (y: number) => `Kalender tahun ${y}`,
  },
  faq: {
    title: (label: string) => `Pertanyaan umum tentang ${label}`,
  },
  longWeekend: {
    heading: (y: number) => `Long weekend & harpitnas ${y}`,
    desc: "Rangkaian hari libur berturut-turut (Sabtu/Minggu + libur nasional + cuti bersama) yang bisa dimanfaatkan untuk liburan tanpa banyak ambil cuti.",
    range: "Rentang",
    total: "Total",
    triggers: "Pemicu",
    fallback: "Akhir pekan + libur",
  },
  table: {
    date: "Tanggal",
    day: "Hari",
    name: "Nama",
    category: "Kategori",
  },
  about: {
    title: "Tentang Kalender Indonesia",
    intro:
      "Kalender Indonesia adalah situs referensi penanggalan lengkap untuk pengguna Indonesia. Mendukung kalender Masehi, Hijriah, Jawa (pasaran & weton), dan Bali (pawukon), serta menampilkan libur nasional, cuti bersama, dan hari penting nasional/internasional.",
    sourceTitle: "Sumber Data Libur",
    sourceBody: (a: number, b: number) =>
      `Data libur nasional dan cuti bersama bersumber dari SKB 3 Menteri (Menag, Menaker, Menpan-RB) untuk tahun ${a}–${b}. Tahun yang belum memiliki SKB resmi ditandai sebagai data perkiraan dan akan diperbarui setelah keputusan resmi diumumkan.`,
    methodTitle: "Metode Konversi Penanggalan",
    methodHijri:
      "Hijriah - menggunakan metode aritmetika sipil (Kuwaiti algorithm) berbasis Julian Day. Tanggal aktual perayaan keagamaan dapat berbeda 1 hari karena rukyatul hilal.",
    methodJawa:
      "Jawa - Sultan Agungan, dengan epoch 1 Sura 1555 J (8 Juli 1633 M) jatuh pada Jumat Legi. Mendukung pasaran, weton, neptu, wuku, dan pranata mangsa.",
    methodBali:
      "Bali - pawukon dengan siklus 210 hari (saptawara, pancawara, triwara, wuku).",
    disclaimerTitle: "Disclaimer",
    disclaimerBody:
      "Data di situs ini bersifat referensi dan dapat mengandung kesalahan. Untuk keperluan resmi seperti administrasi pemerintahan, jadwal ibadah, atau dokumen hukum, mohon verifikasi ulang ke sumber resmi terkait.",
    navTitle: "Navigasi",
  },
  footer: {
    tag: "Kalender Masehi, Hijriah, Jawa, dan Bali lengkap dengan tanggal merah, libur nasional, dan cuti bersama.",
    year: "Tahun",
    tools: "Tools",
    sources: "Sumber",
    aboutLink: "Tentang situs",
    sitemapLink: "Peta situs",
    sourceData: "Data libur: SKB 3 Menteri",
    legal: "Legal",
    contact: "Kontak",
    privacy: "Kebijakan Privasi",
    terms: "Syarat & Ketentuan",
    copy: (y: number) =>
      `© ${y} Kalender Indonesia. Data libur bersifat referensi - verifikasi ulang untuk keperluan resmi.`,
  },
  langSwitch: {
    label: "Bahasa",
    id: "Bahasa Indonesia",
    en: "English",
  },
  mega: {
    now: "Sekarang",
    yearsHeading: "Tahun",
    monthsHeading: "Bulan",
    calculatorTitle: "Tools",
    seeAllCalculators: "Lihat semua kalkulator",
    hijriYearHeading: "Tahun H",
    monthsBadge: (label: string) => `Bulan di ${label}`,
    monthOfX: (m: string) => `Bulan ${m}`,
    noEventsThisMonth: "Tidak ada peringatan bulan ini.",
    seeAllImportant: "Lihat semua hari penting",
    popular: "Populer",
    todayObserved: "Hari ini diperingati:",
    hint: "Arahkan kursor ke tahun untuk melihat bulannya.",
  },
  tools: {
    calculator: { label: "Kalkulator Hari", desc: "Selisih, tambah, umur, hari kerja" },
    weton: { label: "Cek Weton Jawa", desc: "Hari, pasaran, neptu, wuku" },
    jadian: { label: "Hari Jadian", desc: "Sudah berapa lama jadian?" },
    lahir: { label: "Hari Lahir", desc: "Berapa lama kamu hidup?" },
    menikah: { label: "Lama Menikah", desc: "Usia pernikahan kamu" },
    putus: { label: "Hari Putus / Gamon", desc: "Berapa hari sejak putus?" },
    sahabat: { label: "Lama Bersahabat", desc: "Sejak kapan kalian kenal?" },
    ldr: { label: "Lama LDR", desc: "Sudah berapa lama LDR?" },
  },
};

export const en: Dict = {
  brand: "Indonesian Calendar",
  nav: {
    monthNow: "This month",
    today: "Today",
    calculator: "Calculator",
    weton: "Weton Check",
    about: "About",
    sitemap: "Sitemap",
    year: "Year",
    tools: "Calculators & Tools",
    hijri: "Hijri Calendar",
    important: "Notable Days",
    contact: "Contact",
    redDates: "Public Holidays",
    menu: "Menu",
    yearOf: (y: number) => `${y} calendar`,
  },
  common: {
    home: "Home",
    calendar: "Calendar",
    nationalHolidays: "Public Holidays",
    jointLeave: "Joint Leave",
    redDates: "Public Holidays & Red Dates",
    importantDays: "Notable Days",
    workingDay: "Regular working day",
    estimatedData: "estimated data",
    legendLibur: "Public Holiday",
    legendCuti: "Joint Leave",
    legendHari: "Notable Day",
    legendToday: "Today",
    legendCaption: "Legend",
    hijriCalendar: "Hijri Calendar",
    hijriCalendarFull: "Islamic (Hijri) Calendar",
    gregorianCalendar: "Gregorian Calendar",
    viewInHijri: "View in Hijri",
    viewInGregorian: "View in Gregorian",
    importantIslamicDays: "Notable Islamic Days",
    hijriYear: (y: number) => `${y} AH`,
    longWeekend: "Long weekend",
    seeDetail: "See details",
    quickNav: "Quick navigation",
    pickYear: "Pick a year",
    otherYears: "Other years",
    relatedPages: "Related pages",
    detail: "Details",
    download: "Download",
    downloadPdf: "Download PDF",
    selectMonth: "Other months in",
    next: "Next",
    prev: "Previous",
    daysShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    days: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    months: [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ],
    notFound: "Page not found",
    notFoundBody: "This URL does not match a valid year, month, or date format.",
    invalidYear: "Invalid year",
    error: "Something went wrong",
    retry: "Try again",
    goHome: "Back to home",
    summaryHoliday: (libur: number, cuti: number) =>
      `${libur} public holidays, ${cuti} joint leave days`,
    daysCount: (n: number) => `${n} days`,
    holidayCount: (n: number) => `${n} public holidays`,
    cutiCount: (n: number) => `${n} joint leave days`,
    redCount: (l: number, c: number) =>
      `${l} public holidays + ${c} joint leave days`,
    monthOf: (m: string, y: number) => `${m} ${y}`,
    calendarOf: (label: string) => `${label} calendar`,
  },
  home: {
    heroTitle: "Indonesian Calendar",
    heroDesc:
      "Gregorian, Hijri, Javanese and Balinese calendars. Includes public holidays, joint leave, weton, and helpful tools.",
    detailToday: "Today's details",
    monthThis: "This month's calendar",
    yearThis: (y: number) => `${y} calendar`,
    detailTodayDated: (dayName: string, d: number, month: string, y: number) =>
      `Today: ${dayName}, ${d} ${month} ${y}`,
    monthThisDated: (month: string, y: number) => `${month} ${y} calendar`,
    todayLabel: "Today",
    tomorrowLabel: "Tomorrow",
    tomorrowIs: (dayName: string, d: number, month: string, y: number) =>
      `Tomorrow: ${dayName}, ${d} ${month} ${y}`,
    eyebrowToday: "Today's date",
    faqTitle: "Frequently Asked Questions",
    faqQ1: "What day is it today?",
    faqA1: (dayName: string, d: number, month: string, y: number) =>
      `Today is ${dayName}, ${d} ${month} ${y}.`,
    faqQ2: "What is tomorrow's date?",
    faqA2: (dayName: string, d: number, month: string, y: number) =>
      `Tomorrow is ${dayName}, ${d} ${month} ${y}.`,
    faqQ3: "When is the next public holiday?",
    faqA3: (name: string, d: number, month: string, y: number) =>
      `The next public holiday is ${name} on ${d} ${month} ${y}.`,
    faqQ4: "What is joint leave (cuti bersama)?",
    faqA4:
      "Joint leave (cuti bersama) refers to additional days off declared by the Indonesian government alongside public holidays, typically for Eid, Christmas, and New Year.",
    holidayThisMonth: "Holidays this month",
    noHoliday: "No public holidays or joint leave this month.",
    nextHoliday: (y: number) => `Upcoming holidays in ${y}`,
    fullYear: (y: number) => `Full ${y} Calendar`,
    fullYearDesc: (y: number) =>
      `Every month of ${y}. Click a month for details.`,
    seeYearPage: (y: number) => `${y} year page`,
    toolsCalc: "Day Calculator",
    toolsCalcDesc:
      "Date difference, add/subtract days, working days, day name, age calculator.",
    toolsWeton: "Javanese Weton Check",
    toolsWetonDesc:
      "Check weton, neptu, pancasuda, wuku, Javanese date, and pranata mangsa.",
    pdfTitle: "Download Calendar PDF",
    pdfDesc:
      "Available on every year, month, and day page. Choose monthly, full year, or single-day mode. Upload your own logo.",
  },
  date: {
    redLabel: "Public Holiday",
    cutiLabel: "Joint Leave",
    eventToday: "Today's Events",
    hijri: "Hijri",
    jawa: "Javanese",
    bali: "Balinese",
    chinese: "Chinese",
    pranata: "Pranata Mangsa",
    quickWeton: "Check weton for another date",
    quickCalc: "Calculate day difference",
    monthLabel: (m: string, y: number) => `${m} ${y} calendar`,
    yearLabel: (y: number) => `${y} year calendar`,
  },
  faq: {
    title: (label: string) => `FAQ about ${label}`,
  },
  longWeekend: {
    heading: (y: number) => `Long weekends in ${y}`,
    desc: "Consecutive days off (weekend + public holidays + joint leave) that you can use for trips with minimal vacation days.",
    range: "Range",
    total: "Total",
    triggers: "Trigger",
    fallback: "Weekend + holiday",
  },
  table: {
    date: "Date",
    day: "Day",
    name: "Name",
    category: "Category",
  },
  about: {
    title: "About Indonesian Calendar",
    intro:
      "Indonesian Calendar is a complete date reference site supporting Gregorian, Hijri, Javanese (pasaran & weton) and Balinese (pawukon) calendars, plus public holidays, joint leave, and notable days.",
    sourceTitle: "Holiday Data Source",
    sourceBody: (a: number, b: number) =>
      `Public holidays and joint leave data come from the SKB 3 Menteri (Joint Ministerial Decree) for ${a}–${b}. Years without an official decree are marked as estimated data and will be updated when published.`,
    methodTitle: "Calendar Conversion Methods",
    methodHijri:
      "Hijri - civil arithmetic method (Kuwaiti algorithm) based on Julian Day. Actual religious dates may differ by 1 day due to moon sighting (rukyatul hilal).",
    methodJawa:
      "Javanese - Sultan Agungan, with epoch 1 Sura 1555 J (8 July 1633 CE) on Friday Legi. Supports pasaran, weton, neptu, wuku, and pranata mangsa.",
    methodBali:
      "Balinese - pawukon with a 210-day cycle (saptawara, pancawara, triwara, wuku).",
    disclaimerTitle: "Disclaimer",
    disclaimerBody:
      "Data on this site is for reference and may contain errors. For official needs such as government administration, religious schedules, or legal documents, please verify with official sources.",
    navTitle: "Navigation",
  },
  footer: {
    tag: "Gregorian, Hijri, Javanese and Balinese calendars with public holidays and joint leave.",
    year: "Year",
    tools: "Tools",
    sources: "Sources",
    aboutLink: "About this site",
    sitemapLink: "Sitemap",
    sourceData: "Holiday data: SKB 3 Menteri",
    legal: "Legal",
    contact: "Contact",
    privacy: "Privacy Policy",
    terms: "Terms & Conditions",
    copy: (y: number) =>
      `© ${y} Indonesian Calendar. Holiday data is for reference - verify for official needs.`,
  },
  langSwitch: {
    label: "Language",
    id: "Bahasa Indonesia",
    en: "English",
  },
  mega: {
    now: "Current",
    yearsHeading: "Year",
    monthsHeading: "Months",
    calculatorTitle: "Tools",
    seeAllCalculators: "See all calculators",
    hijriYearHeading: "Year AH",
    monthsBadge: (label: string) => `Months in ${label}`,
    monthOfX: (m: string) => `${m} events`,
    noEventsThisMonth: "No notable days this month.",
    seeAllImportant: "See all notable days",
    popular: "Popular",
    todayObserved: "Observed today:",
    hint: "Hover a year to preview its months.",
  },
  tools: {
    calculator: { label: "Day Calculator", desc: "Difference, add days, age, working days" },
    weton: { label: "Javanese Weton Check", desc: "Day, pasaran, neptu, wuku" },
    jadian: { label: "Days Together", desc: "How long have you been dating?" },
    lahir: { label: "Days Alive", desc: "How long have you lived?" },
    menikah: { label: "Marriage Length", desc: "Your wedding anniversary" },
    putus: { label: "Days Since Breakup", desc: "How long since you broke up?" },
    sahabat: { label: "Friendship Duration", desc: "How long have you been friends?" },
    ldr: { label: "Long-Distance Days", desc: "How long have you been LDR?" },
  },
};

export const dicts: Record<Locale, Dict> = { id, en };